import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { jt, t } from 'ttag'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumButton from '../../components/YumButton/YumButton'
import YumVerificationCodeInput from '../../components/YumVerificationCodeInput/YumVerificationCodeInput'
import Typography from '@material-ui/core/Typography'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { ReactComponent as ArrowBackIcon } from '../../assets/v3/icons/arrow-back.svg'
import './VerifyEmail.scss'

type Purpose = 'LOGIN' | 'SIGNUP' | 'PASSWORD_RESET'

export interface IVerifyEmailStateProps {
  loading: boolean
  resending: boolean
  error: string | null
  purpose: Purpose
  email: string
}

export interface IVerifyEmailDispatchProps {
  verifyEmailCode: (payload: { code: string; purpose: Purpose }) => void
  resendVerificationCode: (payload: { purpose: Purpose }) => void
  doLogOut: () => void
}

type VerifyEmailProps = IVerifyEmailStateProps & IVerifyEmailDispatchProps

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

const VerifyEmail: React.FC<VerifyEmailProps> = ({
  loading,
  resending,
  error,
  purpose,
  email,
  verifyEmailCode,
  resendVerificationCode,
  doLogOut,
}) => {
  const [code, setCode] = useState('')
  // The back-end has just emailed a code, so start the cooldown immediately on mount.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS)

  // Decrement the cooldown once per second until it reaches 0.
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((prev) => Math.max(0, prev - 1)), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event) event.preventDefault()
      if (code.length !== CODE_LENGTH) return
      verifyEmailCode({ code, purpose })
    },
    [code, purpose, verifyEmailCode],
  )

  const handleResend = useCallback(() => {
    if (cooldown > 0 || resending) return
    resendVerificationCode({ purpose })
    setCooldown(RESEND_COOLDOWN_SECONDS)
  }, [cooldown, resending, purpose, resendVerificationCode])

  return (
    <Card className="verify-email pt-12">
      <div className="verify-email__inner">
        <a className="yum-back" style={{ cursor: 'pointer' }} onClick={() => doLogOut()}>
          <ArrowBackIcon />
        </a>
        <NotificationsWrapper />
        <form className="verify-email__form" onSubmit={handleSubmit} noValidate>
          <Typography className="h1-desktop" variant="h1" style={{ textAlign: 'center' }}>
            {t`Two-factor authentication`}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 16, marginBottom: 8, opacity: 0.7, textAlign: 'center' }}>
            {email
              ? jt`We've sent a 6-digit verification code to: ${(<strong key="email">{email}</strong>)}`
              : t`We've sent a 6-digit verification code to your email.`}
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16, opacity: 0.5, textAlign: 'center' }}>
            {t`If you can't find the email, please check your spam folder.`}
          </Typography>
          <YumVerificationCodeInput
            value={code}
            onChange={setCode}
            onComplete={(next) => verifyEmailCode({ code: next, purpose })}
            error={!!error}
            disabled={loading}
            autoFocus={true}
            length={CODE_LENGTH}
          />
          <YumButton type="submit" showLoader={loading} disabled={loading} style={{ marginTop: 16 }}>
            {purpose === 'LOGIN' ? t`Log in` : t`Verify`}
          </YumButton>
          <div className="text-center" style={{ marginTop: 16, fontSize: 14 }}>
            <span style={{ color: '#8E8E93' }}>{t`Didn't receive code?`}</span>{' '}
            {cooldown > 0 ? (
              <span style={{ color: '#8E8E93' }}>{t`Resend in ${cooldown}s`}</span>
            ) : (
              <a className="link-blue" style={{ cursor: resending ? 'default' : 'pointer' }} onClick={handleResend}>
                {t`Resend`}
              </a>
            )}
          </div>
        </form>
      </div>
      <DesktopSidebar />
    </Card>
  )
}

export default VerifyEmail
